import {
  IssueType,
  SingleIssueType,
  IssueTypeDataT,
  IssueTypesOrder,
} from '@/routes/settings/issueTypes/types/issueType.ts'
import { api, queryClient } from '@/lib/api-client.ts'
import { AxiosResponse, AxiosError } from 'axios'
import { getCurrentOrganization } from '@/lib/utils.ts'
import useIssueTypeStore from '@/routes/settings/issueTypes/state/issueTypesStore.ts'
import { subscribeToMercureTopic } from '@/lib/helpers/subscribeToMercureTopic.ts'
import createIri from '@/lib/helpers/createIri.ts'

type QuickEditData = {
  name: string | undefined
  color: string | undefined
}

export const organizationIssueTypesQuery = ({ projectId }: { projectId: string | undefined }) => ({
  queryKey: [projectId ? 'projectIssueTypes' : 'organizationIssueTypes'],
  queryFn: () => (projectId ? getProjectIssueTypes(projectId) : getIssueTypes()),
})

export const getOrganizationIssueTypes = async ({
  projectId = undefined,
}: {
  projectId: string | undefined
  view?: string
}) => {
  return await queryClient.ensureQueryData(organizationIssueTypesQuery({ projectId }))
}

export const getProjectIssueTypes = async (projectId: string | undefined) => {
  const { setIssueTypesList } = useIssueTypeStore.getState()
  if (!projectId) {
    return null
  }

  try {
    const response = await api.get(`/projects/${projectId}/issue_types`)
    const issueTypes = response.data as IssueType
    if (issueTypes) {
      setIssueTypesList(issueTypes)
      issueTypes['hydra:member'].forEach((issueType: SingleIssueType) => {
        // const composedTopic = issueType['@id']
        const issueTypeIri = createIri(`issue_types`, issueType.id)

        subscribeToMercureTopic(issueTypeIri || '', event => {
          let data: SingleIssueType
          try {
            data = JSON.parse(event.data) as SingleIssueType
          } catch {
            return
          }
          const issueTypesList = queryClient.getQueryData(['projectIssueTypes'])
          if (issueTypesList) {
            const typedIssueTypesList = issueTypesList as IssueType
            const updatedMembers = typedIssueTypesList['hydra:member'].map(
              (issue: SingleIssueType) => {
                return issue.id === data.id ? data : issue
              },
            )
            queryClient.setQueryData(['projectIssueTypes'], { 'hydra:member': updatedMembers })
            setIssueTypesList({ 'hydra:member': updatedMembers })
          }
        })
      })
    }

    return issueTypes
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}
export const getIssueTypes = async () => {
  const organizationId = getCurrentOrganization()?.organization?.id
  const { setIssueTypesList } = useIssueTypeStore.getState()

  if (!organizationId) {
    return null
  }

  try {
    const response = await api.get(`/organizations/${organizationId}/issue_types`)
    const issueTypes = response.data as IssueType
    if (issueTypes) {
      setIssueTypesList(issueTypes)
      issueTypes['hydra:member'].forEach((issueType: SingleIssueType) => {
        const issueTypeIri = createIri(`issue_types`, issueType.id)
        // const composedTopic = issueType['@id']
        // console.log(`subscribing ...`, issueTypeIri)
        subscribeToMercureTopic(issueTypeIri || '', event => {
          let data: SingleIssueType
          try {
            data = JSON.parse(event.data) as SingleIssueType
          } catch {
            return
          }

          const issueTypesList = queryClient.getQueryData(['organizationIssueTypes'])
          if (issueTypesList) {
            const typedIssueTypesList = issueTypesList as IssueType
            const updatedMembers = typedIssueTypesList['hydra:member'].map(
              (issue: SingleIssueType) => {
                return issue.id === data.id ? data : issue
              },
            )
            queryClient.setQueryData(['organizationIssueTypes'], { 'hydra:member': updatedMembers })
            setIssueTypesList({ 'hydra:member': updatedMembers })
          }
        })
      })
    }

    return issueTypes
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}

export const createIssueType = async (
  newIssueTypeData: IssueTypeDataT,
): Promise<AxiosResponse<SingleIssueType>> => {
  return api.post('/issue_types', newIssueTypeData)
}

export const quickEditIssueType = async (
  issueType: SingleIssueType,
  quickEditData: QuickEditData,
): Promise<AxiosResponse<SingleIssueType>> => {
  return api.patch(`/issue_types/${issueType.id}`, {
    ...quickEditData,
  })
}

export const editIssueType = async (
  issueType: IssueTypeDataT,
): Promise<AxiosResponse<SingleIssueType>> => {
  return api.patch(`/issue_types/${issueType.id}`, issueType)
}

export const updateIssueTypeOrdering = async (
  submitData: {
    issueTypes: IssueTypesOrder[]
  },
  projectId: string | undefined = undefined,
): Promise<AxiosResponse<SingleIssueType>> => {
  if (projectId) {
    return api.patch(`/projects/${projectId}/issue_types/`, submitData)
  }
  const organizationId = getCurrentOrganization()?.organization?.id
  return api.patch(`/organizations/${organizationId}/issue_types/`, submitData)
}

export const deleteIssueType = async (
  issueType: SingleIssueType,
): Promise<AxiosResponse<SingleIssueType>> => {
  return api.delete(`/issue_types/${issueType.id}`)
}
